import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPersonas } from "../../redux/reducers/personasReducer";
import styles from "./ChatComponent.module.css";

const ChatComponent = () => {
    const dispatch = useDispatch();
    const { list: personas, loading, error } = useSelector(state => state.personas);
    
    const [activePersonaId, setActivePersonaId] = useState(
      personas.some(persona => persona.id === localStorage.getItem("activePersonaId"))
        ? localStorage.getItem("activePersonaId")
        : personas[0]?.id || 0
    );
    
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [isSoundOn, setIsSoundOn] = useState(true);
  
    useEffect(() => {
      dispatch(fetchPersonas());
    }, [dispatch]);

    const bottomRef = useRef(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);
  
    useEffect(() => {
      if (personas.length > 0 && activePersonaId === 0) {
        const activePersona = personas.find(p => p.isActive) || personas[0];
        setActivePersonaId(activePersona.id);
        loadPersonaCollections(activePersona.id);
        console.log('activePersona.id changed: ', activePersona.id);
        localStorage.setItem("activePersonaId", activePersona.id);
      }
    }, [personas, activePersonaId]);
  
    useEffect(() => {
      if (activePersonaId) {
        const storedMessages = localStorage.getItem(`messages_persona_${activePersonaId}`);
        if (storedMessages) {
          setMessages(JSON.parse(storedMessages));
        } else {
          setMessages([]);
        }
      }
    }, [activePersonaId]);
  
    useEffect(() => {
      if (activePersonaId) {
        localStorage.setItem(`messages_persona_${activePersonaId}`, JSON.stringify(messages));
      }
    }, [messages, activePersonaId]);

    const sendMessageToBackend = async (message) => {
    const activePersona = personas.find((p) => p.id === activePersonaId);
    if (!activePersona) {
        throw new Error("Active persona not found");
    }
    const dayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });
    const userDateTime = new Date().toLocaleString() + ' ' + dayName;
    const { name, speaking_style_params, speaking_style_words, unique_words_12_months } = activePersona;
      try {
        const response = await fetch('https://staywithme.io/chat-with-persona', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            personaId: activePersonaId,
            message: message,
            persona_name: name,
            speaking_style_params: speaking_style_params,
            speaking_style_words: speaking_style_words,
            unique_words_12_months: unique_words_12_months,
            datetime: userDateTime,
          }),
          credentials: 'include'
        });
  
        if (!response.ok) {
          throw new Error('Failed to send message');
        }
  
        const result = await response.json();
        if (result.success) {
            return result.data;
        }
        else {
            throw new Error('Request failed');
        }

      } catch (error) {
        console.error('Error sending message:', error);
        throw error;
      }
    };

    const loadPersonaCollections = async (personaId) => {
        try {
            let loadCollectionsResponse = await fetch('https://staywithme.io/load-persona-collections', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    personaId: personaId
                }),
                credentials: 'include'
            });
            console.log('loadCollectionsResponse: ', loadCollectionsResponse);
        } catch (error) {
            console.error('Error loading persona collections:', error);
            throw error;
        }
    };

    const getActivePersonaVoiceId = () => {
        const activePersona = personas.find(p => p.id === activePersonaId);
        return activePersona ? activePersona.voiceId : null;
    };

    const getAudioResponse = async (message) => {
        let voice_id = getActivePersonaVoiceId();
        console.log('voice_id of persona: ', voice_id);
        try {
            const response = await fetch('https://staywithme.io/get-audio-response', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    voiceId: voice_id,
                    message: message
                }),
                credentials: 'include'
            });

            if (!response.ok) {throw new Error("Failed to fetch audio");}

            const audioBlob = await response.blob();
            
            const audioUrl = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioUrl);
            
            audio.onended = () => {
                URL.revokeObjectURL(audioUrl);
            };
            
            await audio.play();

        } catch (error) {
            console.error('Error getting audio response:', error);
            throw error;
        }
    };

    const handleSendMessage = async () => {
      if (inputMessage.trim() === "" || isSending) return;
  
      setIsSending(true);
      setSendError(null);
  
      try {

        const userMessage = {
          id: messages.length + 1,
          text: inputMessage,
          sender: "user",
          timestamp: new Date().toISOString()
        };
  
        setMessages(prev => [...prev, userMessage]);
        setInputMessage("");
  
        const response = await sendMessageToBackend(inputMessage);

        const personaMessage = {
          id: messages.length + 2,
          text: response,
          sender: "persona",
          timestamp: new Date().toISOString()
        };

        if (isSoundOn) {
          await getAudioResponse(response);
        }

        setMessages(prev => [...prev, personaMessage]);

      } catch (error) {
        setSendError("Failed to send message. Please try again.");
        console.error('Error in handleSendMessage:', error);
      } finally {
        setIsSending(false);
      }
    };
  
    const handleKeyPress = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSendMessage();
      }
    };
  
    const handlePersonaSwitch = (personaId) => {
      setActivePersonaId(personaId);
      localStorage.setItem("activePersonaId", personaId);
      loadPersonaCollections(personaId);
      setSendError(null);
    };
  
    if (loading) {
      return <div className={styles.loading}>Loading personas...</div>;
    }
  
    if (error) {
      return <div className={styles.error}>Error loading personas: {error}</div>;
    }
  
    const activePersona = personas.find(p => p.id === activePersonaId) || personas[0];
  
    return (
        <div className={styles.chatPage}>
        {/* Mobile Header with Dropdown */}
        <div className={styles.mobileHeader}>
        <div className={styles.dropdownWrapper}>
            <select 
            className={styles.mobilePersonaSelect}
            value={activePersonaId}
            onChange={(e) => handlePersonaSwitch(e.target.value)}
            >
            {personas.map((persona) => (
                <option key={persona.id} value={persona.id}>
                {persona.name}
                </option>
            ))}
            </select>
        </div>
        </div>

        {/* Sidebar - Hidden on Mobile */}
        <div className={styles.sidebar}>
        <div className={styles.chatList}>
            {personas.map((persona) => (
            <div
                key={persona.id}
                className={`${styles.chatItem} ${
                persona.id === activePersonaId ? styles.chatItemActive : ''
                }`}
                onClick={() => handlePersonaSwitch(persona.id)}
            >
                <div className={styles.personaName}>{persona.name}</div>
            </div>
            ))}
        </div>
        <div className={styles.footerLinks}></div>
        </div>

        {/* Chat Section */}
        <div className={styles.chatSection}>
        <div className={styles.chatHeader}>
            <div className={styles.headerContent}>
            <div className={styles.chatTitle}>{activePersona?.name}</div>
            <div className={styles.chatSubtitle}>{activePersona?.language}</div>
            </div>
        </div>

        <div className={styles.chatContent}>
            {messages.map((message) => (
            <div
                key={message.id}
                className={`${styles.message} ${
                message.sender === "user" ? styles.messageUser : styles.messagePersona
                }`}
            >
                <div className={styles.messageText}>{message.text}</div>
            </div>
            ))}
            {sendError && (
            <div className={styles.errorMessage}>{sendError}</div>
            )}
            <div ref={bottomRef} />
        </div>

        <div className={styles.chatInputBlock}>
          <button
            onClick={() => setIsSoundOn(!isSoundOn)}
            className={styles.soundtoggle}
            aria-label={isSoundOn ? "Turn sound off" : "Turn sound on"}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className={isSoundOn ? styles.soundon : styles.soundoff}
            >
              {isSoundOn ? (
                <>
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                </>
              ) : (
                <>
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </>
              )}
            </svg>
          </button>
            <div className={styles.inputWrapper}>
            <input
                type="text"
                className={styles.chatInput}
                placeholder={`Message ${activePersona?.name}...`}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
            />
            {isSending && (
                <div className={styles.sendingIndicator}>
                <div className={styles.loadingDots}>
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
                </div>
            )}
            </div>
        </div>
        </div>
    </div>
    );
  };
  
  export default ChatComponent;